import * as React from 'react';
import { addressBookStyles } from './address-book.styles';
import Paginator from '../common/paginator';
import {
    Button,
    message,
    Modal,
    Input,
    Popconfirm,
    Dropdown,
    Menu,
    Select,
} from 'antd';
import {
    EditOutlined, SearchOutlined, DeleteOutlined, PlusOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { HocOptions } from '../common/generic-hoc.types';
import { StylesProps, ThemeType } from '../../theme/jss-types';
import { addressBookColumns, addressFieldMetadata } from './address-book.constants';
import {
    deleteAddress,
    bulkDeleteAddress,
    downloadAddresses,
    downloadAllAddresses,
} from '../../network/pickup.api';
import { ReduxStore } from '../../reducers/redux.types';
import { bindActionCreators } from 'redux';
import {
    setPagination,
} from '../../actions/generic-action';
import { Buckets, NextOrPrev, Pagination } from '../../library/Types';
import GenericHoc from '../common/generic-hoc';
import { RouteChildrenProps } from 'react-router';
import CreateAddressForm from '../address/create-address';
import CreateAddressForm2 from '../address/create-address2';
import { PickupAddress } from '../pickup/create-pickup/pickup.types';
import Helper from '../../library/Helper';
import { loadAddresses } from '../../actions/address-actions';
import { useDebounce } from 'hooks/use-debounce';
import ExcelUpload from './excel-upload';
import { Master } from 'types/master-data-types';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';
import { paginationOptions } from 'library/constants';

interface AddressBookProps
    extends StylesProps<ReturnType<typeof addressBookStyles>>,
    RouteChildrenProps {
    filters: Record<string, any>;
    loading: boolean;
    uiTheme: ThemeType;
    loadaddresses: (searchString: string, parentCustomerId?: string) => void;
    pagination: Pagination,
    addressList: any,
    setpagination: (bucketId: Buckets.ADDRESSES, newPagination: Partial<Pagination>) => void;
    master: Master,
    autofillAddressFieldsBasedOnAddressHierarchy: boolean,
}

const {
    useState,
    useEffect,
} = React;

const AddressBookPage = (props: AddressBookProps) => {
    const [downloading, setDownloading] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [newAddressVisible, setNewAddressVisible] = useState<boolean>(false);
    const [addressesSelected, setAddressesSelected] = useState<any[]>([]);
    const [editAddress, setEditAddress] = useState<PickupAddress | undefined>();
    const [searchString, setSearchString] = useState<string>('');
    const [toggle, setToggle] = useState<boolean>(true);
    const [excelUploadVisible, setExcelUploadVisible] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const { t } = useTranslation();

    const {
        classes,
        loadaddresses,
        setpagination,
        loading,
        pagination,
        addressList,
        master,
        uiTheme,
        autofillAddressFieldsBasedOnAddressHierarchy,
    } = props;

    const phoneRegex = master?.parts_to_show?.remove_regex ? null : master?.config?.phone_regex;
    const countryWisePhoneRegex = master?.config?.country_wise_phone_regex;
    const debounceTimeSearchString = useDebounce(searchString);
    const showAddressCode = master?.config?.customer_portal_config?.allow_address_code_in_addresses;
    const locationMasterConfig = master?.config?.customer_address_book_config;

    useEffect(() => {
        if (master?.properties?.parent_address_book) {
            loadaddresses(searchString, master?.Customer?.parent_id);
        } else {
            loadaddresses(searchString);
        }
    }, [debounceTimeSearchString, toggle]);

    const renderText = (text: string | number) => {
        return (
            <div
                className={classes.cellValue}
            >
                {text}
            </div>
        );
    };

    const openCreateAddressModal = () => {
        setNewAddressVisible(true);
    };

    const handleDownload = async () => {
        setDownloading(true);
        const selectedAddressToDownload = addressList?.addressBookData
            .filter((item: any) => addressesSelected.some((selected) => selected.id === item.id));

        const fileBuffer = await downloadAddresses(selectedAddressToDownload);

        const fileName = fileBuffer.filename;
        if (fileBuffer.isSuccess) {
            Helper.downloadFileData(
                fileBuffer.data,
                fileName || 'addressbook.xlsx',
                true,
            );
            message.success('Downloaded Successfully');
        } else {
            message.error(fileBuffer.errorMessage);
        }
        setDownloading(false);
    };

    const handleDownloadAll = async () => {
        setDownloading(true);
        const fileBuffer = await downloadAllAddresses('');

        const fileName = fileBuffer.filename;
        if (fileBuffer.isSuccess) {
            Helper.downloadFileData(
                fileBuffer.data,
                fileName || 'addressbook.xlsx',
                true,
            );
            message.success('Downloaded Successfully');
        } else {
            message.error(fileBuffer.errorMessage);
        }

        setDownloading(false);
    };

    const newAddressMenu = (
        <Menu>
            <Menu.Item
                key="profile"
                onClick={() => openCreateAddressModal()}
            >
                {t('single_address')}
            </Menu.Item>
            <Menu.Item
                key="settings"
                onClick={() => setExcelUploadVisible(true)}
            >
                {t('upload_excel_address')}
            </Menu.Item>
        </Menu>
    );

    const downloadAddressMenu = (
        <Menu>
            <Menu.Item
                key="profile"
                onClick={() => handleDownloadAll()}
            >
                All
            </Menu.Item>
            <Menu.Item
                key="settings"
                onClick={() => handleDownload()}
            >
                {t('selected')}
            </Menu.Item>
        </Menu>
    );

    const handleDelete = async (e: any, item: any) => {
        const customerCode = window.localStorage.getItem('userCode') || '';
        Helper.stops(e);
        const result = await deleteAddress({ id: item.id, addressCode: item.addressCode, customerCode });
        if (result.isSuccess) {
            setToggle(!toggle);
            message.success('Address Deleted Successfully');
        } else {
            message.error(result.errorMessage);
        }
    };

    const handleDeleteBulk = async (e: any, addressToDelete: string[]) => {
        Helper.stops(e);
        const customerCode = window.localStorage.getItem('userCode') || '';
        setDeleteLoading(true);
        const result = await bulkDeleteAddress({ data: addressToDelete, customerCode });
        if (result.data.success) {
            setToggle(!toggle);
            message.success('Addresses Deleted Successfully');
        } else {
            message.error('Unable to delete the selected addresses');
        }
        setDeleteLoading(false);
    };

    const renderEditIcon = (item: PickupAddress, theme: ThemeType, isEnabled: boolean) => {
        return (
            <EditOutlined
                onClick={(e) => {
                    if (isEnabled) {
                        Helper.stops(e as any);
                        setEditAddress(item);
                        setNewAddressVisible(true);
                    }
                }}
                style={{ color: isEnabled ? theme.primaryColor : 'lightgray', marginRight: '0 8px' }}
            />
        );
    };

    const renderDeleteIcon = (item: PickupAddress, isEnabled: boolean) => {
        return (isEnabled ? (
            <Popconfirm
                placement="topLeft"
                title={t('delete_address_text')}
                okText={t('ok_text')}
                cancelText={t('cancel')}
                onConfirm={(e) => handleDelete(e, item)}
                onCancel={(e) => Helper.stops(e as any)}
            >
                <DeleteOutlined
                    onClick={(e) => {
                        Helper.stops(e as any);
                    }}
                    style={{ color: 'red' }}
                />
            </Popconfirm>
        ) : (
            <DeleteOutlined
                style={{
                    color: 'lightgray',
                }}
            />
        )
        );
    };

    const renderActions = (row: any, isEnabled: boolean) => {
        return (
            <div>
                {renderEditIcon(row, uiTheme, isEnabled)}
                {renderDeleteIcon(row, isEnabled)}
            </div>
        );
    };

    const renderColumn = (text: any, row: any, column: string) => {
        if (column === 'actions') {
            const isEnabled = (row?.clientId === master?.Customer?.id) && !master?.properties?.disable_address_book_edit;
            return renderActions(row, isEnabled);
        }
        if (typeof text === 'boolean') {
            return renderText(text ? 'Yes' : 'No');
        }
        if (Array.isArray(text)) {
            const formattedText = text?.map((item) => (Array.isArray(item) ? item.join('-') : item)).join(', ');
            return renderText(formattedText);
        }

        // Handle dock_vehicle_mapping display (new format: DockEntry[])
        if (column === 'dockVehicleMapping' && text) {
            try {
                let entries: any[] = [];
                if (Array.isArray(text)) {
                    entries = text;
                } else {
                    const parsed = JSON.parse(text);
                    if (Array.isArray(parsed)) entries = parsed;
                }
                if (entries.length > 0) {
                    const sortedEntries = [...entries]
                        .sort((a, b) => parseInt(a.dockNumber, 10) - parseInt(b.dockNumber, 10))
                        .slice(0, 3);
                    const hasMore = entries.length > 3;
                    return (
                        <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                            {sortedEntries.map((entry: any) => {
                                const vehicleList = Array.isArray(entry.vehicleMakes)
                                    ? entry.vehicleMakes.join(', ')
                                    : '';
                                return (
                                    <div key={entry.dockNumber}>
                                        <b>{`Dock ${entry.dockNumber}:`}</b>
                                        {` ${vehicleList || '-'}`}
                                    </div>
                                );
                            })}
                            {hasMore && <div>...</div>}
                        </div>
                    );
                }
                return renderText(String(text));
            } catch (e) {
                return renderText(String(text));
            }
        }

        // Handle grace_time display (unit is in header)
        if (column === 'graceTime' && text) {
            return renderText(text);
        }

        if (!text || text === null) {
            return <div className={classes.cellNa}>-NA-</div>;
        }
        return renderText(text);
    };

    const getWidth = (id: string) => {
        switch (id) {
            case 'name':
            case 'phone':
            case 'alternatePhone':
            case 'alternatePhone2': return 120;
            case 'notes': return 200;
            case 'serialNumber': return 60;
            case 'timeSlotList': return 220;
            default: return 120;
        }
    };

    const getFixed = (column: string) => {
        switch (column) {
            case 'actions': return 'right';
            default: return undefined;
        }
    };

    const getColumns = (): any[] => {
        const fieldsList = locationMasterConfig?.fields_list;

        // If config is available, build columns dynamically
        if (fieldsList && fieldsList.length > 0) {
            const columns: any[] = [];

            // Add serial number column first
            columns.push({
                key: 'serialNumber',
                title: t('serialNumber'),
                dataIndex: 'serialNumber',
                width: 60,
                ellipsis: true,
                render: (text: string, row: any) => renderColumn(text, row, 'serialNumber'),
            });

            // Add columns based on config (filtered by show_on_dashboard)
            fieldsList.forEach((field) => {
                if (field.show_on_dashboard) {
                    const fieldMeta = addressFieldMetadata[field.key];
                    if (fieldMeta) {
                        // Skip address_id if showAddressCode is false
                        if (field.key === 'address_id' && !showAddressCode) {
                            return;
                        }
                        // Add unit suffix to grace_time header
                        let columnTitle = t(fieldMeta.label);
                        if (field.key === 'grace_time') {
                            columnTitle = `${columnTitle} (sec)`;
                        }
                        columns.push({
                            key: fieldMeta.dataIndex,
                            title: columnTitle,
                            dataIndex: fieldMeta.dataIndex,
                            width: fieldMeta.width || getWidth(fieldMeta.dataIndex),
                            ellipsis: true,
                            render: (text: string, row: any) => renderColumn(text, row, fieldMeta.dataIndex),
                        });
                    }
                }
            });

            // Add actions column at the end
            columns.push({
                key: 'actions',
                title: t('actions'),
                dataIndex: 'actions',
                width: 120,
                ellipsis: true,
                fixed: 'right',
                render: (text: string, row: any) => renderColumn(text, row, 'actions'),
            });

            return columns;
        }

        // Fallback to existing hardcoded columns if config is not available
        const addressTableColumns = showAddressCode
            ? addressBookColumns
            : omit(addressBookColumns, 'addressCode');
        const columns: any = Object.keys(addressTableColumns).map((column: string) => {
            return {
                key: column,
                title: t(column),
                dataIndex: column,
                width: getWidth(column),
                ellipsis: true,
                fixed: getFixed(column),
                render: (text: string, row: any) => renderColumn(text, row, column),
            };
        });
        return columns;
    };

    const renderTable = () => {
        return (
            <FixedHeaderTable
                bordered={false}
                pagination={false}
                loading={loading}
                rowKey={(row) => row.id}
                columns={getColumns()}
                scroll={{
                    y: 'calc(100% - 110px)',
                }}
                style={{
                    height: 'calc(100% - 80px)',
                    background: '#ffffff',
                }}
                rowSelection={{
                    onSelect: (row, selected: any) => {
                        let newArray = [...addressesSelected];
                        if (selected && !newArray.some((item) => item.id === row.id)) {
                            newArray.push({ id: row.id, addressCode: row.addressCode });
                        } else {
                            newArray = newArray.filter((item) => item.id !== row.id);
                        }
                        setAddressesSelected(newArray);
                    },
                    onSelectAll: (selected, selectedRows) => {
                        const newArray = selectedRows.map((item) => ({
                            id: item.id,
                            addressCode: item.addressCode,
                        }));
                        setAddressesSelected(newArray);
                    },
                }}
                dataSource={addressList?.addressBookData || []}
            />
        );
    };

    const handleSearchQuery = (e : any) => {
        const newPagination = {
            ...pagination,
            currentPageNumber: 1,
        };
        setpagination(Buckets.ADDRESSES, newPagination);
        setSearchString(e.target.value);
    };

    const renderSearchFilter = () => {
        return (
            <Input
                className={classes.searchInput}
                type="search"
                value={searchString}
                onChange={(e: any) => handleSearchQuery(e)}
                placeholder={t('search_for_addresses')}
                suffix={<SearchOutlined />}
            />
        );
    };

    const renderLeftFilters = () => {
        return (
            <div className={classes.leftFilters}>
                {renderSearchFilter()}
            </div>
        );
    };

    const renderDownload = () => {
        return (
            <Button
                type="primary"
                loading={downloading}
                className={classes.downloadBtn}
                icon={<DownloadOutlined />}
            >
                {t('download')}
            </Button>
        );
    };

    const handlePagination = (nextOrPrev: NextOrPrev, resultPerPage?: number | undefined) => {
        const { currentPageNumber } = pagination;
        let newPageNumber = currentPageNumber;
        if (nextOrPrev === 'first') {
            newPageNumber = 1;
        } else if (nextOrPrev === 'next') {
            newPageNumber = currentPageNumber + 1;
        } else {
            newPageNumber = currentPageNumber - 1;
        }
        const newPagination = {
            ...pagination,
            nextOrPrev,
            currentPageNumber: newPageNumber,
        };
        if (resultPerPage) {
            newPagination.resultPerPage = Number(resultPerPage);
        }
        setpagination(Buckets.ADDRESSES, newPagination);
        setToggle(!toggle);
    };

    const renderPaginator = () => {
        return (
            <div className={classes.paginationGroup}>
                <div>
                    {t('showing')}
                    <Select
                        className={classes.resultPerPage}
                        value={pagination.resultPerPage.toString()}
                        onChange={(val: string) => handlePagination('first', val as any)}
                        options={paginationOptions}
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    />
                    {t('rows_per_page')}
                </div>
                <Paginator
                    currentPageNumber={pagination.currentPageNumber}
                    isNextPresent={addressList?.isNextPresent}
                    onFirstClick={() => handlePagination('first')}
                    onNextClick={() => handlePagination('next')}
                    onPrevClick={() => handlePagination('prev')}
                />
            </div>
        );
    };

    const renderFooter = () => {
        return (
            <div className={classes.footer}>
                {renderPaginator()}
            </div>
        );
    };

    const renderDeleteButton = () => {
        return (
            <Button
                type="primary"
                loading={deleteLoading}
                className={classes.downloadBtn}
                onClick={(e) => handleDeleteBulk(e, addressesSelected)}
                icon={<DeleteOutlined />}
            >
                {t('delete_address')}
            </Button>
        );
    };

    const handleBulkEdit = () => {
        setExcelUploadVisible(true);
        setIsEdit(true);
    };

    const renderEditButton = () => {
        if (!showAddressCode) return null;
        return (
            <Button
                type="primary"
                className={classes.downloadBtn}
                onClick={() => handleBulkEdit()}
                icon={<EditOutlined />}
            >
                {t('bulk_edit_address')}
            </Button>
        );
    };

    const renderNewAddress = () => {
        return (
            <Button
                type="primary"
                className={classes.newAddressBtn}
                icon={<PlusOutlined />}
            >
                {t('add_address')}
            </Button>
        );
    };

    const cancelModal = () => {
        setNewAddressVisible(false);
        setEditAddress(undefined);
    };

    const renderCreateNewAddress = () => {
        if (!newAddressVisible) {
            return null;
        }
        return (
            <Modal
                className={classes.modal}
                closable
                open
                title={editAddress?.id ? t('edit_address') : t('create_address')}
                width="700px"
                footer={null}
                onCancel={() => cancelModal()}
            >
                {autofillAddressFieldsBasedOnAddressHierarchy
                    ? (
                        <CreateAddressForm2
                            editData={editAddress}
                            onClose={() => {
                                cancelModal();
                                setToggle(!toggle);
                            }}
                            phoneRegex={phoneRegex}
                            isInternationalAddress={
                                (editAddress?.isInternational || false)
                            }
                            countryWisePhoneRegex={countryWisePhoneRegex}
                            showSaveButtonAtBottom
                            locationMasterConfig={locationMasterConfig}
                        />
                    ) : (
                        <CreateAddressForm
                            editData={editAddress}
                            onClose={() => {
                                cancelModal();
                                setToggle(!toggle);
                            }}
                            phoneRegex={phoneRegex}
                            isInternationalAddress={
                                (editAddress?.isInternational || false)
                            }
                            countryWisePhoneRegex={countryWisePhoneRegex}
                            showSaveButtonAtBottom
                            locationMasterConfig={locationMasterConfig}
                        />
                    )}
            </Modal>
        );
    };


    const renderCreateAddressDropdown = () => {
        return (
            <Dropdown
                overlay={newAddressMenu}
                trigger={['click']}
            >
                {renderNewAddress()}
            </Dropdown>
        );
    };

    const renderDownloadDropdown = () => {
        return (
            <Dropdown
                overlay={downloadAddressMenu}
                trigger={['click']}
            >
                {renderDownload()}
            </Dropdown>
        );
    };

    const renderRightFilters = () => {
        return (
            <div className={classes.rightFilters}>
                {(!master?.properties?.disable_address_book_edit) ? renderDeleteButton() : null}
                {renderEditButton()}
                {renderDownloadDropdown()}
                {(!master?.properties?.disable_address_book_edit) ? renderCreateAddressDropdown() : null}
            </div>
        );
    };

    const renderExtraFilters = () => {
        return (
            <div className={classes.extraFilters}>
                {renderLeftFilters()}
                {renderRightFilters()}
            </div>
        );
    };

    const renderFilters = () => {
        return (
            <div className={classes.filters}>
                {renderExtraFilters()}
            </div>
        );
    };

    const onCloseDrawer = () => {
        setExcelUploadVisible(false);
        setIsEdit(false);
    };

    const renderExcelUpload = () => {
        if (!excelUploadVisible) {
            return null;
        }

        return (
            <ExcelUpload
                onClose={() => onCloseDrawer()}
                isEdit={isEdit}
            />
        );
    };

    return (
        <div className={classes.main}>
            {renderFilters()}
            <>
                {renderTable()}
                {renderFooter()}
            </>
            {renderCreateNewAddress()}
            {renderExcelUpload()}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { generic } = state;
    const { ADDRESSES } = generic;
    const {
        loading,
        data,
        pagination,
    } = ADDRESSES;
    return {
        loading,
        addressList: data,
        uiTheme: state.uiTheme,
        pagination,
        autofillAddressFieldsBasedOnAddressHierarchy: state?.master?.parts_to_show
            ?.autofill_address_fields_based_on_address_hierarchy,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    const actions = {
        loadaddresses: loadAddresses,
        setpagination: setPagination,
    };
    return bindActionCreators(actions, dispatch);
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: addressBookStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
        mapDispatchToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

const AddressBooks = GenericHoc(hocConfig)(AddressBookPage);
export default AddressBooks;
